import { DateTime } from "luxon";
import { JobPostEditDefaultValues, JobPostFormInput, JobPostOwner } from "./types";
import { fullyDecodeHtmlEntities } from "@/lib/htmlHelpers";
import { toDateOnlyInTimezone } from "@/lib/dateHelpers";
import { sortBy } from "lodash";
import { ApplicationQuestion, Option } from "@/pages/_layouts/jobs/types";

// Helper function to decode potentially problematic HTML strings
const decodeHtmlString = (inputString: string | undefined | null): string => {
  let decodedString = inputString || "";
  if (decodedString) {
    // Process only if there's content
    // Try to repair if it's a JSON stringified HTML (e.g. contains \\u00xx or has escaped quotes like "\\"<p>...<\\/p>\\"")
    if (decodedString.includes("\\\\u00") || (decodedString.startsWith('"') && decodedString.endsWith('"'))) {
      try {
        decodedString = JSON.parse(decodedString);
      } catch (e) {
        // console.info is intentionally commented out here to avoid spamming the console for fields that might not be JSON.
        // The main goal is to ensure fullyDecodeHtmlEntities runs.
        // console.info(`Failed to JSON.parse string, proceeding with entity decoding for: ${inputString ? inputString.substring(0, 50) + "..." : "undefined"}`, e);
      }
    }
    // After potential JSON.parse, or if it wasn't JSON stringified, decode HTML entities.
    // This also handles cases where the content from JSON.parse might itself contain HTML entities.
    decodedString = fullyDecodeHtmlEntities(decodedString);
  }
  return decodedString;
};

export const isValidDomain = (hostname: string): boolean => {
  // RFC 1035 compliant domain validation with following rules:
  // 1. Allows international domains (IDN)
  // 2. Each label (part between dots) must:
  //    - Start with a letter/number
  //    - Can contain hyphens (but not at start/end)
  //    - Be between 1-63 characters
  // 3. TLD must be at least 2 characters
  // 4. Total domain length must not exceed 253 characters
  const domainPattern = new RegExp(
    "^" + // Start of string
      "(?!-)[A-Za-z0-9-]{1,63}(?<!-)" + // First label (max 63 chars, no leading/trailing hyphens)
      "(?:\\.[A-Za-z0-9-]{1,63}(?<!-)){0,}" + // Optional additional labels
      "\\.[A-Za-z]{2,}" + // TLD (at least 2 chars, letters only)
      "$", // End of string
    "u" // Unicode flag for IDN support
  );

  if (hostname.length > 253) return false;

  return domainPattern.test(hostname);
};

export const ensureUrlProtocol = (value: string): string => {
  let newValue = value;

  // If the value is shorter than http://, reset to http://
  if (newValue.length < 7) {
    return "http://";
  }

  // Ensure prefix exists with proper double slashes
  if (!newValue.match(/^https?:\/\//)) {
    // Remove any malformed protocol attempts before adding correct one
    newValue = newValue.replace(/^https?:?\/?\/?(.*)/i, "$1");
    newValue = "http://" + newValue;
  }

  return newValue;
};

export const validateUrl = (value: string): true | string => {
  // First check for proper protocol format with double slashes
  if (!value.match(/^https?:\/\//)) {
    return "URL must start with http:// or https://";
  }

  try {
    const url = new URL(value);

    return isValidDomain(url.hostname) || "Please enter a valid domain (e.g. example.com)";
  } catch {
    return "Please enter a valid URL";
  }
};

export const SUBMISSION_DEADLINE_ZONE = "America/New_York";

export const normalizeSubmissionDeadlineDate = (submissionDeadline: string | Date | null | undefined) =>
  toDateOnlyInTimezone(submissionDeadline, SUBMISSION_DEADLINE_ZONE);

export const toSingleDateSubmissionDeadline = (submissionDeadline: string | Date | null | undefined) => {
  const normalizedSubmissionDeadline = normalizeSubmissionDeadlineDate(submissionDeadline);

  return {
    startDate: normalizedSubmissionDeadline,
    endDate: normalizedSubmissionDeadline
  };
};

export const formatSubmissionDeadlineForMutation = (submissionDeadline: string | null | undefined) =>
  normalizeSubmissionDeadlineDate(submissionDeadline) ?? null;

export const isSubmissionDeadlineInPast = (submissionDeadline: string | null | undefined) => {
  const normalizedSubmissionDeadline = normalizeSubmissionDeadlineDate(submissionDeadline);

  if (!normalizedSubmissionDeadline) {
    return false;
  }

  return (
    DateTime.fromISO(normalizedSubmissionDeadline, { zone: SUBMISSION_DEADLINE_ZONE }).endOf("day") <
    DateTime.now().setZone(SUBMISSION_DEADLINE_ZONE)
  );
};

export const formatJobPostForForm = (jobPost: any): JobPostEditDefaultValues => {
  const decodedOverview = decodeHtmlString(jobPost.overview);
  const decodedBenefits = decodeHtmlString(jobPost.benefits);
  const decodedAdditionalIncome = decodeHtmlString(jobPost.additionalIncome);
  const decodedRequiredExperienceDescription = decodeHtmlString(jobPost.requiredExperienceDescription);
  const decodedEssentialResponsibilities = decodeHtmlString(jobPost.essentialResponsibilities);
  let owners: JobPostOwner[] = [];
  if (Array.isArray(jobPost.owners)) {
    owners = jobPost.owners;
  }
  if (owners.length === 0 && jobPost.owner) {
    owners = [jobPost.owner];
  }
  // For sendForReviewDescription, it's initialized differently if not present in jobPost,
  // but we should still decode it if it comes from jobPost.
  // However, in the current structure, it's set to "" if not from jobPost.
  // The current structure initializes sendForReviewDescription to "" in the formattedData object,
  // so we'll decode jobPost.sendForReviewDescription if it exists, otherwise default to "".

  const formattedData: JobPostEditDefaultValues = {
    id: jobPost.id,
    name: jobPost.name,
    overview: decodedOverview,
    stage: jobPost.stage,
    status: jobPost.status,
    facilityId: jobPost.facility?.id || "",
    facilityName: jobPost.facility?.name || "",
    serviceLevel: jobPost.serviceLevel,
    employerId: jobPost.employer?.id || "",
    pointOfContact: jobPost.pointOfContact,
    submissionDeadline: normalizeSubmissionDeadlineDate(jobPost.submissionDeadline) ?? "",
    compensation: jobPost.compensation,
    requiredEducation: jobPost.requiredEducation || "N/A",
    requiredExperienceInYears: jobPost.requiredExperienceInYears,
    requiredExperienceDescription: decodedRequiredExperienceDescription,
    essentialResponsibilities: decodedEssentialResponsibilities,
    hideCompensationFromJobBoard: jobPost.hideCompensationFromJobBoard,
    applicantGroup: jobPost.applicantGroup,
    applyVia: jobPost.applyVia,
    paySchedule: jobPost.paySchedule,
    positionType: jobPost.positionType,
    otherSections: jobPost.otherSections,
    otherStates: jobPost.otherStates,
    additionalPgaEducation: jobPost.additionalPgaEducation || "N/A",
    externalJobUrl: jobPost.externalJobUrl,
    benefits: decodedBenefits,
    additionalIncome: decodedAdditionalIncome,
    category: jobPost.category.name,
    sendForReview: false,
    sendForReviewDescription: jobPost.sendForReviewDescription || "",
    owners: owners.map((owner: JobPostOwner) => ({
      id: owner.id,
      name: owner.name,
      email: owner.email,
      photo: owner.photo
    })),
    ...(jobPost.applicationForm && {
      application: {
        id: jobPost.applicationForm.id,
        isFormLocked: jobPost.applicationForm.isFormLocked,
        questions: sortBy(jobPost.applicationForm.questions, "position").map((question: ApplicationQuestion) => ({
          id: question.id,
          position: question.position,
          content: question.content,
          questionType: question.questionType,
          options: question.options.map((option: Option) => ({
            id: option.id,
            position: option.position,
            content: option.content
          }))
        }))
      }
    })
  };

  // Add employerData as a non-type property (it will be used by the form but isn't in the type)
  (formattedData as any).employerData = jobPost.employer;

  if (jobPost.section?.id) {
    (formattedData as any).sectionId = jobPost.section.id;
  }

  return formattedData;
};

export const formatFormDataForMutation = (formData: JobPostFormInput, jobPostId: string) => {
  const { facilityName, employerData, submissionDeadline, owners, ...restFormData } = formData;

  // Apply HTML entity decoding to all relevant text fields before mutation
  const decodedOverview = fullyDecodeHtmlEntities(restFormData.overview || "");
  const decodedBenefits = fullyDecodeHtmlEntities(restFormData.benefits || "");
  const decodedAdditionalIncome = fullyDecodeHtmlEntities(restFormData.additionalIncome || "");
  const decodedRequiredExperienceDescription = fullyDecodeHtmlEntities(
    restFormData.requiredExperienceDescription || ""
  );
  const decodedEssentialResponsibilities = fullyDecodeHtmlEntities(restFormData.essentialResponsibilities || "");

  return {
    id: jobPostId,
    name: restFormData.name,
    overview: decodedOverview,
    stage: restFormData.stage,
    serviceLevel: restFormData.serviceLevel,
    facilityId: restFormData.facilityId,
    benefits: decodedBenefits,
    additionalIncome: decodedAdditionalIncome,
    employerId: restFormData.employerId || null,
    category: restFormData.category,
    requiredEducation: restFormData.requiredEducation === "N/A" ? null : restFormData.requiredEducation,
    requiredExperienceInYears: parseInt(restFormData.requiredExperienceInYears, 10) || 0,
    requiredExperienceDescription: decodedRequiredExperienceDescription,
    essentialResponsibilities: decodedEssentialResponsibilities,
    hideCompensationFromJobBoard: restFormData.hideCompensationFromJobBoard,
    applicantGroup: restFormData.applicantGroup,
    applyVia: restFormData.applyVia,
    paySchedule: restFormData.paySchedule,
    positionType: restFormData.positionType,
    otherSections: restFormData.otherSections,
    otherStates: restFormData.otherStates,
    additionalPgaEducation: restFormData.additionalPgaEducation === "N/A" ? null : restFormData.additionalPgaEducation,
    externalJobUrl: restFormData.externalJobUrl,

    // Review Request
    sendForReview: restFormData.sendForReview,
    sendForReviewDescription: restFormData.sendForReviewDescription || "",

    // Compensation fields
    baseMaximumCompensation:
      restFormData.compensation.baseMaximum !== undefined ? restFormData.compensation.baseMaximum / 100 : null,
    baseMinimumCompensation:
      restFormData.compensation.baseMinimum !== undefined ? restFormData.compensation.baseMinimum / 100 : null,
    totalAnticipatedCompensation: restFormData.compensation?.totalAnticipated
      ? restFormData.compensation.totalAnticipated / 100
      : undefined,
    currencyUnit: restFormData.compensation.currencyUnit,

    // Point of contact fields
    pointOfContactName: restFormData.pointOfContact.name,
    pointOfContactEmail: restFormData.pointOfContact.email,
    pointOfContactTitle: restFormData.pointOfContact.title,
    pointOfContactPhone: restFormData.pointOfContact.phone,

    // Date fields
    submissionDeadline: formatSubmissionDeadlineForMutation(submissionDeadline?.startDate),

    ...(owners && owners.length > 0 ? { ownerIds: owners.map((owner) => owner.id) } : {})
  };
};

export const isAdvancedOrExecutiveWithQuestions = (formData: JobPostFormInput) => {
  const isAdvancedOrExecutive = formData.serviceLevel === "Advanced" || formData.serviceLevel === "Executive";
  const hasQuestions =
    Array.isArray(formData.application?.questions) && (formData.application?.questions?.length ?? 0) > 0;

  return isAdvancedOrExecutive && hasQuestions;
};
